/**
 * 智能客服 - 渠道物料 接口定义
 */
import { apiGet, apiPut, apiPost, apiDelete } from '@/utils/request';
import { parsePaginatedResponse } from '@/utils/apiResponse';
import { LARGE_PAGE_SIZE } from '@/constants/api';

interface DyMiniCreateItem {
  account_id: number;
  title: string;
  content: string;
  image_url: string;
  mini_app_id: string;
  page_path: string;
}

/**
 * 解析渠道物料的 content 字段
 * 后端列表/详情接口可能返回 JSON 字符串或对象，统一转为对象
 */
export function parseMaterialContent(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
    } catch {
      /* ignore */
    }
  }
  return {};
}

export const channelMaterialsApi = {
  list: (params: Record<string, unknown> = {}) =>
    apiGet('/channel-materials', { per_page: LARGE_PAGE_SIZE, page: 1, ...params }).then((d) =>
      parsePaginatedResponse(d),
    ),
  show: (id: number) => apiGet(`/channel-materials/${id}`),
  /**
   * 创建渠道物料（通用接口）
   *
   * 后端会根据 account_id 自动识别渠道来源（source），并校验该渠道是否支持传入的 type。
   *
   * 支持的物料类型（type）：
   * | type | 名称       | 适用渠道              | content 来源              |
   * |------|------------|-----------------------|---------------------------|
   * | 5    | 图文消息   | wx                    | 手动创建（必填 title+url）|
   * | 6    | 留资卡     | dy, xhs               | 平台 API 返回 → 保存      |
   * | 9    | 小程序卡片 | dy, wx, fmc, wxkf     | 手动创建（必填 app_id+page_path+title） |
   * | 10   | 商品卡     | dy                    | 平台 API 返回 → 保存      |
   * | 11   | 收集号码   | dy                    | 平台 API 返回 → 保存      |
   * | 12   | 获客链接   | fmc, wxkf             | 手动创建（必填 url）      |
   * | 13   | 名片       | xhs                   | 平台 API 返回 → 保存      |
   * | 14   | 笔记       | xhs                   | 平台 API 返回 → 保存      |
   * | 15   | 落地页     | xhs                   | 平台 API 返回 → 保存      |
   * | 16   | 交易卡     | xhs                   | 平台 API 返回 → 保存      |
   */
  create: (data: {
    /** 账号 ID，后端据此识别渠道来源（source）并校验是否支持当前 type */
    account_id: number | string;
    /** 物料名称，最长 128 字符 */
    name: string;
    /** 物料类型，详见上方支持类型表 */
    type: number;
    /** 结构化内容，平台 API 返回数据原样保存或手动组装 */
    content: Record<string, unknown>;
    /** 封面图 / 存储地址（可选） */
    url?: string;
  }) => apiPost('/channel-materials', data),
  /** 更新渠道物料（PUT 部分更新，字段均可选） */
  update: (
    id: number,
    data: {
      name?: string;
      type?: number;
      url?: string;
      content?: Record<string, unknown>;
    },
  ) => apiPut(`/channel-materials/${id}`, data),
  /** 删除渠道物料 */
  delete: (id: number) => apiDelete(`/channel-materials/${id}`),
  /** 抖音商品卡片列表（游标分页）；product_id 为精确搜索，传了之后接口忽略 page/page_size */
  dyProductList: (params: {
    account_id: number;
    product_id?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiGet<{ data?: Record<string, unknown>[]; page?: string }>(
      '/channel-materials/dy-product-list',
      params,
    ),
  /** 抖音小程序列表 */
  dyMiniList: (params: { account_id?: number; page?: number; page_size?: number }) =>
    apiGet<{ list?: unknown[]; total?: number }>('/channel-materials/dy-mini-list', params),
  /** 创建抖音小程序卡片模板（支持单条或批量） */
  dyMiniCreate: (data: DyMiniCreateItem | DyMiniCreateItem[]) =>
    apiPost('/channel-materials/dy-mini-create', data),
  /** 批量更新抖音小程序卡片模板 */
  dyMiniUpdate: (
    data: {
      id: number;
      account_id?: number;
      title?: string;
      content?: string;
      image_url?: string;
      mini_app_id?: string;
      page_path?: string;
    }[],
  ) => apiPut('/channel-materials/dy-mini-update', data),
  /** 删除抖音小程序卡片模板 */
  dyMiniDelete: (ids: number[]) => apiPost('/channel-materials/dy-mini-delete', { ids }),
  /** 查询抖音小程序卡片审核状态 */
  dyMiniStatus: (id: number) =>
    apiGet<{ id: number; card_template_id: string; status: number }>(
      `/channel-materials/dy-mini-status/${id}`,
    ),
  /** 绑定抖音来客生活服务账号 */
  dyLifeBindAccount: (data: {
    account_id: number;
    life_account_id: string;
    name?: string;
    status?: number;
  }) => apiPost('/channel-materials/dy-life-bindAccount', data),
  /** 小红书卡片/物料列表（统一端点），type 支持枚举值或字符串 key */
  xhsCardList: (params: {
    page?: number;
    page_size?: number;
    account_id?: number | string;
    type?: number | string;
    note_id?: string;
  }) => apiGet<{ list?: unknown[]; total?: number }>('/channel-materials/xhs-card-list', params),
};
